import { useState, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

interface CaseReportMedicalListProps {
    value: any[];
    isReadOnly?: boolean;
    involvedPersons?: any[];
    onChange?: (patients: any[]) => void;
    onAdd?: () => void;
    onEdit?: (patient: any) => void;
    onOpenChecklist?: (data: { patient: any; checklistType: string }) => void;
}

export function CaseReportMedicalList({
    value: patients,
    isReadOnly = false,
    involvedPersons = [],
    onChange,
    onAdd,
    onEdit,
    onOpenChecklist,
}: CaseReportMedicalListProps) {
    const { theme } = useTheme();
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({ open: false, item: null });

    const confirmDelete = useCallback((patient: any) => {
        setDeleteConfirm({ open: true, item: patient });
    }, []);

    const doDelete = useCallback(() => {
        const item = deleteConfirm.item;
        if (item) {
            onChange?.(patients.filter((p: any) => p.tempid !== item.tempid));
        }
        setDeleteConfirm({ open: false, item: null });
    }, [deleteConfirm.item, patients, onChange]);

    const cancelDelete = useCallback(() => {
        setDeleteConfirm({ open: false, item: null });
    }, []);

    const getHospitalizedLabel = useCallback((value: any): string => {
        if (value === 1 || value === '1') return 'Yes';
        if (value === 2 || value === '2') return 'No';
        return 'N/A';
    }, []);

    const getHospitalizedClass = useCallback((value: any): string => {
        if (value === 1 || value === '1') return 'hospitalized-yes';
        if (value === 2 || value === '2') return 'hospitalized-no';
        return 'hospitalized-na';
    }, []);

    const getPatientName = useCallback((patientRecord: any): string => {
        if (patientRecord.patient && involvedPersons) {
            const person = involvedPersons.find((p: any) =>
                p.id === patientRecord.patient ||
                p.id === Number(patientRecord.patient)
            );
            if (person) {
                return person.name || person.fullName ||
                    `${person.firstName || ''} ${person.lastName || ''}`.trim() ||
                    'Unknown';
            }
        }
        return 'Unknown';
    }, [involvedPersons]);

    const openMedicalChecklist = useCallback((patient: any, checklistType: string) => {
        onOpenChecklist?.({ patient, checklistType });
    }, [onOpenChecklist]);

    return (
        <div className={'case-report-medical-list' + (theme ? ' ' + theme : '')}>
            <div className="header">
                <h1>Patient Details</h1>
                {!isReadOnly && (
                    <button onClick={() => onAdd?.()} className="btn-add" type="button">
                        <i className="fas fa-plus"></i> PATIENT
                    </button>
                )}
            </div>
            <div className="table-container">
                {patients.length === 0 ? (
                    <div className="empty-state"><p>No patients added</p></div>
                ) : (
                    <div className="patient-items">
                        {patients.map((patient: any) => (
                            <div key={patient.tempid} className="patient-item">
                                <div className="patient-primary">
                                    <div className="primary-name"><div className="patient-name">{getPatientName(patient)}</div></div>
                                    <div className="primary-status">
                                        <span className={'hospitalized-badge ' + getHospitalizedClass(patient.hospitalized)}>{getHospitalizedLabel(patient.hospitalized)}</span>
                                    </div>
                                    {!isReadOnly && (
                                        <div className="primary-actions">
                                            <button className="btn-edit" onClick={() => onEdit?.(patient)}>Edit</button>
                                            <button className="btn-delete" onClick={() => confirmDelete(patient)}>Delete</button>
                                        </div>
                                    )}
                                </div>
                                <div className="patient-details">
                                    <div className="detail-grid">
                                        <div className="detail-field detail-symptoms">
                                            <span className="detail-label">Symptoms / Injuries</span>
                                            <span className="detail-value multiline-text">{patient.symptoms || patient.symptomsOrInjuriesTxt || '\u2014'}</span>
                                        </div>
                                        <div className="detail-checklists">
                                            <span className="detail-label">Medical Checklists</span>
                                            <div className="checklists-grid">
                                                <button className="btn-checklist" onClick={() => openMedicalChecklist(patient, 'patientHistory')}><i className="fas fa-clipboard-list"></i><span>Patient History</span></button>
                                                <button className="btn-checklist" onClick={() => openMedicalChecklist(patient, 'abdominalDigestive')}><i className="fas fa-clipboard-list"></i><span>Abdominal / Digestive</span></button>
                                                <button className="btn-checklist" onClick={() => openMedicalChecklist(patient, 'cardiovascular')}><i className="fas fa-clipboard-list"></i><span>Cardiovascular</span></button>
                                                <button className="btn-checklist" onClick={() => openMedicalChecklist(patient, 'genitalUrinary')}><i className="fas fa-clipboard-list"></i><span>Genital / Urinary</span></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="mobile-cards">
                {patients.length === 0 ? (
                    <div className="empty-state"><p>No patients added</p></div>
                ) : (
                    patients.map((patient: any) => (
                        <div key={'mobile-' + patient.tempid} className="card">
                            <div className="card-header">
                                <span className="card-patient-name">{getPatientName(patient)}</span>
                                <span className={'hospitalized-badge ' + getHospitalizedClass(patient.hospitalized)}>{getHospitalizedLabel(patient.hospitalized)}</span>
                            </div>
                            <div className="card-grid">
                                <div className="card-field card-field-full">
                                    <div className="card-field-label">Symptoms / Injuries</div>
                                    <div className="card-field-value multiline-text">{patient.symptoms || patient.symptomsOrInjuriesTxt || '\u2014'}</div>
                                </div>
                            </div>
                            <div className="card-checklists">
                                <div className="card-checklists-label">Medical Checklists</div>
                                <div className="card-checklists-grid">
                                    <button className="btn-checklist-mobile" onClick={() => openMedicalChecklist(patient, 'patientHistory')}><i className="fas fa-clipboard-list"></i><span>Patient History</span></button>
                                    <button className="btn-checklist-mobile" onClick={() => openMedicalChecklist(patient, 'abdominalDigestive')}><i className="fas fa-clipboard-list"></i><span>Abdominal / Digestive</span></button>
                                    <button className="btn-checklist-mobile" onClick={() => openMedicalChecklist(patient, 'cardiovascular')}><i className="fas fa-clipboard-list"></i><span>Cardiovascular</span></button>
                                    <button className="btn-checklist-mobile" onClick={() => openMedicalChecklist(patient, 'genitalUrinary')}><i className="fas fa-clipboard-list"></i><span>Genital / Urinary</span></button>
                                </div>
                            </div>
                            {!isReadOnly && (
                                <div className="card-actions">
                                    <button className="btn-action btn-edit" onClick={() => onEdit?.(patient)}><i className="fa fa-edit"></i> Edit</button>
                                    <button className="btn-action btn-delete" onClick={() => confirmDelete(patient)}><i className="fa fa-trash"></i> Remove</button>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
            <ConfirmModal
                open={deleteConfirm.open}
                title="Remove Patient?"
                messageHtml="Are you sure you want to remove this patient from the report?"
                confirmText="Remove"
                confirmClass="danger"
                cancelText="Cancel"
                autoFocus="cancel"
                scrollLock={true}
                onConfirm={doDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
}

(window as any).CaseReportMedicalListReact = CaseReportMedicalList;
